import { StandardEvents, CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';
import { ThemeEvents } from '@theme/events';

const WARRANTY_HANDLE = 'extension-garantie-24-mois';
const BATTERY_HANDLE = 'batterie-neuve-iphone';
const isAddon = (handle) => [WARRANTY_HANDLE, BATTERY_HANDLE].includes(handle);
const RATE_BPS = 1500;
const MIN_CENTS = 900;
const MAX_CENTS = 39900;
const locale = ['fr', 'en', 'de'].includes(document.documentElement.lang.slice(0, 2))
  ? document.documentElement.lang.slice(0, 2)
  : 'fr';
const messages = (() => {
  try { return JSON.parse(document.querySelector('[data-sp-warranty-messages]')?.textContent || '{}'); }
  catch { return {}; }
})();

const jsonOf = (root, selector, fallback) => {
  try { return JSON.parse(root.querySelector(selector)?.textContent || ''); }
  catch { return fallback; }
};

const warrantyPrice = (phoneCents) => {
  const raw = Math.round(Number(phoneCents) * RATE_BPS / 10000);
  const lower = Math.floor((raw - 900) / 1000) * 1000 + 900;
  const upper = lower + 1000;
  const result = raw - lower < upper - raw ? lower : upper;
  return result >= MIN_CENTS && result <= MAX_CENTS ? result : null;
};

const money = (cents) => new Intl.NumberFormat(locale + '-CH', {
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
}).format(cents / 100) + ' CHF';

const sectionsForCart = () => [...document.querySelectorAll('cart-items-component')]
  .map((node) => node instanceof HTMLElement ? node.dataset.sectionId : '')
  .filter(Boolean);

const dispatchCartUpdate = (source, element, lines) => {
  const deferred = CartLinesUpdateEvent.createPromise();
  element.dispatchEvent(new CartLinesUpdateEvent({ action: 'add', context: 'cart', lines, promise: deferred.promise }));
  return deferred;
};

const cartAfter = async (payload, deferred, source) => {
  const response = await fetch(Theme.routes.cart_url + '.js', { headers: { Accept: 'application/json' }, credentials: 'same-origin' });
  if (!response.ok) throw new Error(messages.refreshFailed || messages.failed);
  const cart = await response.json();
  deferred.resolve({
    cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
    detail: { sections: payload.sections, items: cart.items, itemCount: cart.item_count, source, didError: false },
  });
  return cart;
};

let quantitySyncTimer;
let quantitySyncRunning = false;
const scheduleQuantitySync = () => {
  window.clearTimeout(quantitySyncTimer);
  quantitySyncTimer = window.setTimeout(syncWarrantyQuantities, 350);
};

const syncWarrantyQuantities = async () => {
  if (quantitySyncRunning) return;
  quantitySyncRunning = true;
  try {
    const response = await fetch(Theme.routes.cart_url + '.js', { headers: { Accept: 'application/json' }, credentials: 'same-origin' });
    if (!response.ok) return;
    let cart = await response.json();
    const mismatches = cart.items.filter((item) => {
      const handle = item.handle || item.product_handle;
      const parentKey = item.parent_relationship?.parent_key || item.parent_relationship?.parent?.key;
      const parent = cart.items.find((candidate) => candidate.key === parentKey);
      return isAddon(handle) && parent && item.quantity !== parent.quantity;
    });
    if (!mismatches.length) return;
    const updates = Object.fromEntries(mismatches.map((item) => {
      const parentKey = item.parent_relationship?.parent_key || item.parent_relationship?.parent?.key;
      return [item.key, cart.items.find((candidate) => candidate.key === parentKey).quantity];
    }));
    const deferred = dispatchCartUpdate('shopyphone-warranty-quantity', document.documentElement, []);
    const change = await fetch(Theme.routes.cart_update_url, {
      method: 'POST',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({ updates, sections: sectionsForCart(), sections_url: window.location.pathname }),
    });
    const payload = await change.json();
    if (!change.ok || payload.status) throw new Error(payload.description || payload.message || 'Synchronisation impossible');
    cart = payload.items ? payload : cart;
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: { sections: payload.sections, items: cart.items, itemCount: cart.item_count, source: 'shopyphone-warranty-quantity', didError: false },
    });
  } catch (error) {
    console.warn('ShopyPhone warranty quantity sync:', error);
  } finally {
    quantitySyncRunning = false;
  }
};

const nestedWarrantyRow = (parentRow) => {
  const component = parentRow.closest('cart-items-component');
  const parentKey = parentRow.dataset.key;
  if (!component || !parentKey || parentRow.dataset.parentKey) return null;
  return [...component.querySelectorAll('[data-parent-key]')].find((row) =>
    row instanceof HTMLElement
      && row.dataset.parentKey === parentKey
      && [WARRANTY_HANDLE, BATTERY_HANDLE].some((handle) => row.querySelector('.cart-items__title[href*="/products/' + handle + '"]'))) || null;
};

const prepareNestedQuantityControls = () => {
  document.querySelectorAll('cart-items-component [data-key]').forEach((row) => {
    if (!(row instanceof HTMLElement)) return;
    const childRow = nestedWarrantyRow(row);
    if (!(childRow instanceof HTMLElement)) return;
    const childTitle = childRow.querySelector('.cart-items__title');
    if (childTitle instanceof HTMLElement) childTitle.dataset.spWarrantyLinkLabel = messages.linked;
    row.querySelectorAll('button[name="plus"], button[name="minus"]').forEach((button) => button.removeAttribute('on:click'));
    row.querySelectorAll('input[name="quantity"], input[name="updates[]"]').forEach((input) => input.removeAttribute('on:blur'));
  });
};

const updateNestedQuantity = async (parentRow, childRow, quantity, source) => {
  const parentKey = parentRow.dataset.key;
  const childKey = childRow.dataset.key;
  if (!parentKey || !childKey) return;
  const updates = { [parentKey]: quantity, [childKey]: quantity };
  parentRow.closest('cart-items-component')?.querySelectorAll('[data-parent-key]').forEach((row) => {
    if (row.dataset.parentKey === parentKey && row.dataset.key && [WARRANTY_HANDLE, BATTERY_HANDLE].some((handle) => row.querySelector('.cart-items__title[href*="/products/' + handle + '"]'))) updates[row.dataset.key] = quantity;
  });
  const deferred = dispatchCartUpdate('shopyphone-warranty-quantity', source, []);
  try {
    const response = await fetch(Theme.routes.cart_update_url, {
      method: 'POST',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({ updates, sections: sectionsForCart(), sections_url: window.location.pathname }),
    });
    const payload = await response.json();
    if (!response.ok || payload.status) throw new Error(payload.description || payload.message || 'Synchronisation impossible');
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(payload),
      detail: { sections: payload.sections, items: payload.items, itemCount: payload.item_count, source: 'shopyphone-warranty-quantity', didError: false },
    });
  } catch (error) {
    deferred.reject(error);
    const message = error instanceof Error ? error.message : 'Synchronisation impossible';
    source.dispatchEvent(new CartErrorEvent({ error: message, code: 'INVALID' }));
  }
};

const onNestedQuantityClick = (event) => {
  if (!(event.target instanceof Element)) return;
  const button = event.target.closest('cart-items-component button[name="plus"], cart-items-component button[name="minus"]');
  const parentRow = button?.closest('[data-key]');
  const childRow = parentRow instanceof HTMLElement ? nestedWarrantyRow(parentRow) : null;
  if (!(button instanceof HTMLButtonElement) || !(parentRow instanceof HTMLElement) || !(childRow instanceof HTMLElement)) return;
  const input = parentRow.querySelector('input[name="quantity"], input[name="updates[]"]');
  const current = Math.max(1, Number(input?.value || 1));
  const quantity = button.name === 'plus' ? current + 1 : Math.max(0, current - 1);
  event.preventDefault();
  event.stopImmediatePropagation();
  void updateNestedQuantity(parentRow, childRow, quantity, button);
};

const onNestedQuantityChange = (event) => {
  if (!(event.target instanceof HTMLInputElement) || !['quantity', 'updates[]'].includes(event.target.name)) return;
  const parentRow = event.target.closest('cart-items-component [data-key]');
  const childRow = parentRow instanceof HTMLElement ? nestedWarrantyRow(parentRow) : null;
  if (!(parentRow instanceof HTMLElement) || !(childRow instanceof HTMLElement)) return;
  event.preventDefault();
  event.stopImmediatePropagation();
  void updateNestedQuantity(parentRow, childRow, Math.max(0, Number(event.target.value || 0)), event.target);
};

if (!window.__shopyphoneWarrantyQuantitySync) {
  window.__shopyphoneWarrantyQuantitySync = true;
  document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
    event.promise?.then(() => {
      scheduleQuantitySync();
      requestAnimationFrame(prepareNestedQuantityControls);
    }).catch(() => {});
  });
  document.addEventListener(ThemeEvents.cartSectionRestored, prepareNestedQuantityControls);
  // Run before Horizon's document capture handlers so linked lines update once, together.
  window.addEventListener('click', onNestedQuantityClick, true);
  window.addEventListener('change', onNestedQuantityChange, true);
  document.addEventListener('change', (event) => {
    if (event.target instanceof Element && event.target.closest('cart-items-component')) scheduleQuantitySync();
  }, true);
  requestAnimationFrame(prepareNestedQuantityControls);
}

class ShopyphoneWarrantySelector extends HTMLElement {
  connectedCallback() {
    this.phoneVariants = jsonOf(this, '[data-sp-phone-variants]', {});
    this.warrantyVariants = jsonOf(this, '[data-sp-warranty-variants]', []);
    this.scope = this.closest('product-information') || this.closest('section') || document;
    this.form = this.scope.querySelector('form[action*="/cart/add"]');
    this.addEventListener('change', this.onChoice);
    this.scope.addEventListener('change', this.onVariantChange);
    this.scope.addEventListener('input', this.onQuantityInput, true);
    this.scope.addEventListener('click', this.onQuantityClick, true);
    window.addEventListener('submit', this.onSubmit, true);
    this.render();
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.onChoice);
    this.scope?.removeEventListener('change', this.onVariantChange);
    this.scope?.removeEventListener('input', this.onQuantityInput, true);
    this.scope?.removeEventListener('click', this.onQuantityClick, true);
    window.removeEventListener('submit', this.onSubmit, true);
  }

  phoneVariantId = () => String(this.form?.querySelector('[name="id"]')?.value || this.scope?.querySelector('product-form-component [name="id"]')?.value || '');
  phonePrice = () => Number(this.phoneVariants[this.phoneVariantId()] || 0);
  selected = () => this.querySelector('input[value="24"]')?.checked === true;
  addonProperties = () => ({ _shopyphone_warranty: '24m' });
  selectedAddons = () => [...this.scope.querySelectorAll('shopyphone-warranty-selector, shopyphone-battery-selector')]
    .filter((selector) => selector.form === this.form && selector.selected?.());
  configurationKey = () => this.selectedAddons().map((addon) => Object.entries(addon.addonProperties())[0].join(':')).sort().join('|');
  tier = () => {
    const price = warrantyPrice(this.phonePrice());
    if (price === null) return null;
    return this.warrantyVariants.find((variant) => Number(variant.price) === price) || null;
  };

  onChoice = () => this.render();
  onVariantChange = () => requestAnimationFrame(() => this.render());
  onQuantityInput = (event) => {
    if (event.target instanceof HTMLInputElement && event.target.name === 'quantity') this.render();
  };
  onQuantityClick = (event) => {
    if (!(event.target instanceof Element) || !event.target.closest('quantity-selector-component')) return;
    window.setTimeout(() => this.render(), 0);
  };
  render() {
    this.form = this.scope.querySelector('form[action*="/cart/add"]') || this.form;
    const tier = this.tier();
    const selectedInput = this.querySelector('input[name="sp-warranty-choice"]:checked');
    const pill = this.querySelector('.variant-option__button-label__pill');
    if (selectedInput && pill && pill.previousElementSibling !== selectedInput) selectedInput.after(pill);
    const price = this.querySelector('[data-sp-warranty-price]');
    if (price) price.textContent = tier ? '(+' + money(Number(tier.price)) + ')' : '(' + messages.unavailable + ')';
    const paid = this.querySelector('input[value="24"]');
    if (paid) {
      paid.disabled = !tier;
      paid.dataset.optionAvailable = String(Boolean(tier));
      paid.setAttribute('aria-disabled', String(!tier));
      paid.setAttribute('aria-label', tier ? '24 ' + messages.months + ' ' + price?.textContent : '24 ' + messages.months + ' ' + messages.unavailable);
    }
    const unavailable = this.querySelector('[data-sp-warranty-unavailable]');
    if (unavailable) unavailable.toggleAttribute('hidden', Boolean(tier));
    this.syncAcceleratedFields();
  }

  syncAcceleratedFields() {
    if (!this.form) return;
    this.form.querySelectorAll('[data-sp-warranty-additional]').forEach((node) => node.remove());
    const configuration = this.configurationKey();
    if (configuration) {
      const input = document.createElement('input');
      input.type = 'hidden'; input.name = 'properties[_shopyphone_configuration]'; input.value = configuration; input.dataset.spWarrantyAdditional = '';
      this.form.append(input);
    }
    const quantity = String(Math.max(1, Number(this.form.querySelector('[name="quantity"]')?.value || 1)));
    this.selectedAddons().filter((addon) => addon.tier()).forEach((addon, index) => {
      const prefix = 'shopify-additional-items[' + (index + 1) + ']';
      const values = {
        id: addon.tier().id,
        quantity,
        parent_id: this.phoneVariantId(),
        properties: JSON.stringify(Object.entries(addon.addonProperties()).map(([key, value]) => ({ key, value }))),
      };
      for (const [key, value] of Object.entries(values)) {
        const input = document.createElement('input');
        input.type = 'hidden'; input.name = prefix + '[' + key + ']'; input.value = String(value); input.dataset.spWarrantyAdditional = '';
        this.form.append(input);
      }
    });
  }

  onSubmit = async (event) => {
    if (!(event.target instanceof HTMLFormElement) || event.target !== this.form || !this.selectedAddons().length) return;
    // Preserve Horizon's native click animation and replace only its single-line form submission.
    event.preventDefault();
    event.stopImmediatePropagation();
    void this.addSelected();
  };

  addSelected = async () => {
    const addons = this.selectedAddons();
    if (!addons.length || !this.form || this.form.dataset.spAdding) return;
    if (addons.some((addon) => !addon.tier())) {
      this.dispatchEvent(new CartErrorEvent({ error: messages.unavailable, code: 'INVALID' }));
      return;
    }
    this.form.dataset.spAdding = 'true';
    this.adding = true;
    const formData = new FormData(this.form);
    const phoneId = this.phoneVariantId();
    const quantity = Math.max(1, Number(formData.get('quantity') || 1));
    const button = this.form.querySelector('[type="submit"]');
    if (button) { button.disabled = true; button.setAttribute('aria-busy', 'true'); }
    const lines = [
      { merchandiseId: phoneId, quantity },
      ...addons.map((addon) => ({ merchandiseId: String(addon.tier().id), quantity })),
    ];
    const deferred = dispatchCartUpdate('shopyphone-warranty-product', this, lines);
    try {
      const properties = {};
      for (const [key, value] of formData.entries()) {
        const match = /^properties\[(.+)\]$/.exec(key);
        if (match?.[1] && typeof value === 'string') properties[match[1]] = value;
      }
      properties._shopyphone_configuration = this.configurationKey();
      const body = {
        items: [
          { id: phoneId, quantity, ...(Object.keys(properties).length ? { properties } : {}) },
          ...addons.map((addon) => ({ id: String(addon.tier().id), quantity, parent_id: phoneId, properties: addon.addonProperties() })),
        ],
        sections: sectionsForCart(), sections_url: window.location.pathname,
      };
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST', headers: { Accept: 'application/json', 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' }, body: JSON.stringify(body),
      });
      const payload = await response.json();
      if (!response.ok || payload.status) throw new Error(payload.description || payload.message || messages.failed);
      await cartAfter(payload, deferred, 'shopyphone-warranty-product');
    } catch (error) {
      deferred.reject(error);
      const message = error instanceof Error ? error.message : messages.failed;
      this.dispatchEvent(new CartErrorEvent({ error: message, code: 'INVALID' }));
      const live = this.querySelector('[data-sp-warranty-live]');
      if (live) live.textContent = message;
    } finally {
      this.adding = false;
      delete this.form.dataset.spAdding;
      if (button) { button.disabled = false; button.removeAttribute('aria-busy'); }
    }
  };
}

if (!customElements.get('shopyphone-warranty-selector')) customElements.define('shopyphone-warranty-selector', ShopyphoneWarrantySelector);

class ShopyphoneBatterySelector extends ShopyphoneWarrantySelector {
  selected = () => this.querySelector('input[value="new"]')?.checked === true;
  tier = () => this.dataset.variantId ? { id: this.dataset.variantId, price: 3500 } : null;
  addonProperties = () => ({ _shopyphone_battery: 'new' });

  render() {
    this.form = this.scope.querySelector('form[action*="/cart/add"]') || this.form;
    const checked = this.querySelector('input:checked');
    const pill = this.querySelector('.variant-option__button-label__pill');
    if (checked && pill && pill.previousElementSibling !== checked) checked.after(pill);
    this.syncAcceleratedFields();
  }
}
if (!customElements.get('shopyphone-battery-selector')) customElements.define('shopyphone-battery-selector', ShopyphoneBatterySelector);
